<template>
    <!-- Заставка загрузки в стиле сайта: знак Qanot Sharq (из favicon.svg) парит, под ним по пунктиру летит самолётик (как в подвале).
         Показывается через usePageLoader(); заменяет белый экран и тёмную заставку движка WX -->
    <Transition name="page-loader">
        <div v-if="state.visible" class="page-loader" role="status" aria-live="polite">
            <div class="page-loader__content">
                <svg class="page-loader__mark" viewBox="110 320 810 400" aria-hidden="true">
                    <path fill="#28396E"
                        d="M774.178 605C741.658 604.626 286.386 596.709 122 448.453C129.665 447.303 136.425 443.887 136.425 443.887C136.425 443.887 155.201 415.016 182.435 415.796C202.328 416.364 254.517 429.778 254.517 429.778C254.517 429.778 227.781 391.352 248.79 365.454C308.867 298.408 483.346 335.341 533.098 343.396C582.85 351.458 689.637 370.906 737.863 376.767C786.089 382.629 885.505 388.107 903.596 387.869C921.688 387.625 893.494 409.412 831.875 408.883C770.264 408.368 715.039 401.898 715.039 401.898C715.039 401.898 715.597 418.214 688.7 422.992C640.892 430.4 581.421 416.251 581.421 416.251C581.421 416.251 598.648 435.422 563.648 447.039C523.144 460.487 476.064 453.343 476.064 453.343C476.064 453.343 489.445 467.121 477.43 477.374C461.429 491.034 416.802 495.934 416.802 495.934C416.802 495.934 480.862 535.833 556.999 536.321C640.175 536.858 740.309 511.244 740.309 511.244L737.803 523.041L648.316 541.458C648.316 541.458 635.263 544.411 629.268 548.185C622.907 552.057 620.362 555.586 620.029 561.691C618.521 581.404 646.661 584.039 646.661 584.039C646.661 584.039 703.587 594.104 791.127 587.881C785.677 593.867 780.021 599.577 774.178 605Z" />
                    <path class="page-loader__wave page-loader__wave--blue" fill="#4582CB"
                        d="M132 477C308.212 605.856 605.917 640.263 720 648.943C690.364 665.906 658.166 678.26 624.799 687C515.669 666.57 298.831 611.423 132 477Z" />
                    <path class="page-loader__wave page-loader__wave--green" fill="#43B53B"
                        d="M418.239 703.48C333.8 660.787 219.354 592.354 132 477C256.544 602.536 425.338 673.216 525 702.949C488.647 705.699 452.513 705.486 418.239 703.48Z" />
                </svg>
                <!-- маршрут: самолёт летит по пунктиру, пройденный путь закрашивается цветами волн знака.
                     Только CSS-анимации transform/opacity у HTML-блоков: браузер выполняет их отдельно от основного потока,
                     и полёт не замирает, пока страница грузит и запускает тяжёлый модуль WX (SVG-анимации замирали) -->
                <div class="page-loader__route" aria-hidden="true">
                    <svg class="page-loader__svg" viewBox="0 0 240 32">
                        <g fill="none" stroke-width="2" stroke-linecap="round">
                            <path :d="ROUTE" stroke="#D3DAE6" stroke-dasharray="1 6" />
                            <circle cx="7" cy="18" r="3.5" stroke="#43B53B" />
                            <circle cx="233" cy="18" r="3.5" stroke="#4582CB" />
                        </g>
                    </svg>
                    <div class="page-loader__flight">
                        <!-- окно сдвигается вправо вслед за самолётом, а содержимое — навстречу, поэтому стоит на месте -->
                        <div class="page-loader__trail">
                            <div class="page-loader__trail-inner">
                                <svg class="page-loader__svg" viewBox="0 0 240 32">
                                    <defs>
                                        <linearGradient id="page-loader-trail" gradientUnits="userSpaceOnUse" x1="12"
                                            x2="228" y1="0" y2="0">
                                            <stop offset="0" stop-color="#43B53B" />
                                            <stop offset="1" stop-color="#4582CB" />
                                        </linearGradient>
                                    </defs>
                                    <path :d="ROUTE" fill="none" stroke="url(#page-loader-trail)" stroke-width="2"
                                        stroke-linecap="round" stroke-dasharray="1 6" />
                                </svg>
                            </div>
                        </div>
                        <div class="page-loader__plane">
                            <svg viewBox="0 0 24 24">
                                <path transform="rotate(90 12 12)" fill="#28396E" :d="PLANE" />
                            </svg>
                        </div>
                    </div>
                </div>
                <p class="page-loader__text">{{ state.message || t('loaderDefault') }}</p>
            </div>
        </div>
    </Transition>
</template>

<script setup>
const { t } = useI18n()
const { state } = usePageLoader()

// пунктир маршрута (в координатах блока 240×32); кадры полёта в стилях рассчитаны по этой кривой —
// при её изменении пересчитать keyframes page-loader-plane / -trail / -trail-inner
const ROUTE = 'M12 18C50 9 90 6 120 12S190 26 228 18'
const PLANE = 'M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5z'
</script>

<style lang="scss">
.page-loader {
    position: fixed;
    inset: 0;
    // выше окон движка WX (MUI до ~1500)
    z-index: 2000;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(247, 248, 250, 0.9);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);

    &__content {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 18px;
    }

    &__mark {
        width: 132px;
        height: auto;
        animation: page-loader-float 2.6s ease-in-out infinite;
    }

    &__wave {
        animation: page-loader-wave 2.6s ease-in-out infinite;
        transform-origin: 132px 477px;
    }

    &__wave--green {
        animation-delay: 0.15s;
    }

    // полёт — 2,4 с на круг: 80 % времени самолёт летит с разгоном и торможением (кадры по кривой),
    // затем гаснет вместе с закрашенным путём у правой точки и вылетает снова.
    // Анимации не отключаются при «уменьшении движения» — это индикатор загрузки
    &__route {
        position: relative;
        width: 240px;
        height: 32px;
        margin-top: -6px;
    }

    &__svg {
        display: block;
        width: 240px;
        height: 32px;
    }

    &__flight {
        position: absolute;
        inset: 0;
        animation: page-loader-fade 2.4s linear infinite;
    }

    &__trail {
        position: absolute;
        inset: 0;
        overflow: hidden;
        animation: page-loader-trail 2.4s linear infinite;
        will-change: transform;
    }

    &__trail-inner {
        animation: page-loader-trail-inner 2.4s linear infinite;
        will-change: transform;
    }

    &__plane {
        position: absolute;
        top: 0;
        left: 0;
        width: 20px;
        height: 20px;
        animation: page-loader-plane 2.4s linear infinite;
        will-change: transform;

        svg {
            display: block;
            width: 100%;
            height: 100%;
        }
    }

    &__text {
        font-size: 15px;
        line-height: 20px;
        color: #203562;
        text-align: center;
        padding: 0 16px;
    }
}

// полноэкранная заставка движка WX (тёмно-синяя с gif) — прячем, вместо неё наша (useWxLoaderSync).
// Сам блок остаётся на месте и, как и задумано движком, не даёт кликать, пока идёт загрузка
#root [data-test-id="simple-loader"][class*="fullScreen"] {
    opacity: 0 !important;
}

// появление и исчезновение — плавно
.page-loader-enter-active {
    transition: opacity 0.2s ease;
}

.page-loader-leave-active {
    transition: opacity 0.45s ease;
}

.page-loader-enter-from,
.page-loader-leave-to {
    opacity: 0;
}

@keyframes page-loader-float {
    0%,
    100% {
        transform: translateY(0) rotate(0);
    }

    50% {
        transform: translateY(-8px) rotate(-2deg);
    }
}

@keyframes page-loader-fade {
    0% { opacity: 0; }
    6% { opacity: 1; }
    78% { opacity: 1; }
    90%, 100% { opacity: 0; }
}

// центр самолёта (20×20) в точках кривой ROUTE, равномерно по длине пути с плавным разгоном и торможением
@keyframes page-loader-plane {
    0% { transform: translate(2px, 8px) rotate(-13.3deg); }
    5% { transform: translate(4px, 7.5px) rotate(-13deg); }
    10% { transform: translate(10px, 6.2px) rotate(-12deg); }
    15% { transform: translate(19.8px, 4.3px) rotate(-10.4deg); }
    20% { transform: translate(33.3px, 2px) rotate(-8.2deg); }
    25% { transform: translate(49.5px, 0.1px) rotate(-5.3deg); }
    30% { transform: translate(68.3px, -1px) rotate(-1.5deg); }
    35% { transform: translate(88.9px, -0.7px) rotate(3.7deg); }
    40% { transform: translate(110px, 2px) rotate(11.3deg); }
    45% { transform: translate(130.9px, 6px) rotate(10.1deg); }
    50% { transform: translate(150.8px, 9.1px) rotate(7.3deg); }
    55% { transform: translate(169.7px, 10.9px) rotate(3.5deg); }
    60% { transform: translate(186.2px, 11.3px) rotate(-0.8deg); }
    65% { transform: translate(199.7px, 10.7px) rotate(-5deg); }
    70% { transform: translate(209.7px, 9.5px) rotate(-8.6deg); }
    75% { transform: translate(215.7px, 8.5px) rotate(-11deg); }
    80%, 100% { transform: translate(217.7px, 8.1px) rotate(-11.8deg); }
}

// правый край окна закраски — на x самолёта
@keyframes page-loader-trail {
    0% { transform: translateX(-228px); }
    5% { transform: translateX(-226px); }
    10% { transform: translateX(-220px); }
    15% { transform: translateX(-210.2px); }
    20% { transform: translateX(-196.7px); }
    25% { transform: translateX(-180.5px); }
    30% { transform: translateX(-161.7px); }
    35% { transform: translateX(-141.1px); }
    40% { transform: translateX(-120px); }
    45% { transform: translateX(-99.1px); }
    50% { transform: translateX(-79.2px); }
    55% { transform: translateX(-60.3px); }
    60% { transform: translateX(-43.8px); }
    65% { transform: translateX(-30.3px); }
    70% { transform: translateX(-20.3px); }
    75% { transform: translateX(-14.3px); }
    80%, 100% { transform: translateX(-12.3px); }
}

@keyframes page-loader-trail-inner {
    0% { transform: translateX(228px); }
    5% { transform: translateX(226px); }
    10% { transform: translateX(220px); }
    15% { transform: translateX(210.2px); }
    20% { transform: translateX(196.7px); }
    25% { transform: translateX(180.5px); }
    30% { transform: translateX(161.7px); }
    35% { transform: translateX(141.1px); }
    40% { transform: translateX(120px); }
    45% { transform: translateX(99.1px); }
    50% { transform: translateX(79.2px); }
    55% { transform: translateX(60.3px); }
    60% { transform: translateX(43.8px); }
    65% { transform: translateX(30.3px); }
    70% { transform: translateX(20.3px); }
    75% { transform: translateX(14.3px); }
    80%, 100% { transform: translateX(12.3px); }
}

@keyframes page-loader-wave {
    0%,
    100% {
        transform: scaleX(1);
        opacity: 1;
    }

    50% {
        transform: scaleX(0.9);
        opacity: 0.75;
    }
}
</style>
